import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import agent from "../../utils/axios";
import { formatMoney } from "../../utils/format";
import { Callout, ConfirmDialog } from "../../components/ui";
import { billCount, convertTitle } from "./billHelpers";

export interface QuickBooksCompany {
  name: string;
  realmId: string;
  hasValidToken: boolean;
  accessTokenExpired: boolean;
}

/** One bill listed in the dialog: "Payee, $amount". */
export interface ConvertBillSummary {
  name: string;
  amount: number | null | undefined;
}

interface CompanySelectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (company: QuickBooksCompany) => void;
  /** The bills being converted, listed as "Payee, $amount". */
  bills?: ConvertBillSummary[];
  /** Spinner on the action and no closing while the conversion runs. */
  busy?: boolean;
}

/**
 * "Confirm with a choice" (AB#6872 dialogs) for Convert to check: lists the bills and lets
 * the person pick which QuickBooks company (RealmId) pays them by check. Mirrors the COMPASS
 * company-selection pattern: the list comes from the backend (per-environment config), and
 * each company shows whether it still needs a sign-in so the caller can skip the popup.
 */
function CompanySelectDialog({
  open,
  onClose,
  onConfirm,
  bills,
  busy = false,
}: CompanySelectDialogProps) {
  const [companies, setCompanies] = useState<QuickBooksCompany[]>([]);
  // Track the selection by row INDEX, not realmId. Lower environments map every company to
  // the same sandbox realmId, so using realmId as the option value makes the entries
  // indistinguishable to MUI Select (the selection won't stick). Index is always unique.
  const [selectedIndex, setSelectedIndex] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let active = true;
    async function loadCompanies() {
      setLoading(true);
      setError(null);
      try {
        const result: QuickBooksCompany[] =
          await agent.quickbooks.getCompanies();
        if (!active) return;
        setCompanies(result);
        // Auto-select when only one company is configured.
        if (result.length === 1) {
          setSelectedIndex("0");
        }
      } catch (e) {
        if (active) setError("Unable to load QuickBooks companies.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCompanies();
    return () => {
      active = false;
    };
  }, [open]);

  const handleConfirm = () => {
    const company = companies[Number(selectedIndex)];
    if (company) onConfirm(company);
  };

  const count = bills?.length ?? 0;
  const confirmLabel =
    count > 0 ? `Convert ${billCount(count)}` : "Convert to check";
  const body =
    count === 1
      ? "It leaves Tipalti and is paid by check from the QuickBooks company you pick."
      : "They leave Tipalti and are paid by check from the QuickBooks company you pick.";

  return (
    <ConfirmDialog
      open={open}
      variant="confirm"
      title={convertTitle(count)}
      body={body}
      confirmLabel={confirmLabel}
      onConfirm={handleConfirm}
      onClose={onClose}
      busy={busy}
      confirmDisabled={
        loading || selectedIndex === "" || !companies[Number(selectedIndex)]
      }
      width={440}
    >
      {count > 0 && (
        <Box
          component="ul"
          aria-label="Bills to convert"
          sx={{
            m: 0,
            pl: 5,
            maxHeight: 180,
            overflowY: "auto",
            fontSize: "0.84375rem",
            color: "text.primary",
            "& li": { py: 0.25 },
          }}
        >
          {bills?.map((bill, index) => (
            <li key={index}>
              {bill.name || "Unnamed payee"}, {formatMoney(bill.amount)}
            </li>
          ))}
        </Box>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading companies…
        </Typography>
      ) : error ? (
        <Callout tone="err" role="alert">
          {error}
        </Callout>
      ) : companies.length === 0 ? (
        <Callout tone="warn">No QuickBooks companies are configured.</Callout>
      ) : (
        <FormControl fullWidth size="small" required>
          <InputLabel id="qb-company-label">QuickBooks company</InputLabel>
          <Select
            labelId="qb-company-label"
            label="QuickBooks company"
            value={selectedIndex}
            disabled={busy}
            onChange={(e) => setSelectedIndex(e.target.value)}
          >
            {companies.map((company, index) => (
              <MenuItem
                key={`${company.realmId}-${index}`}
                value={String(index)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <span>{company.name}</span>
                  {!company.hasValidToken && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      (sign-in required)
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </ConfirmDialog>
  );
}

export default CompanySelectDialog;
